import { Box, Link } from '@factorialco/deprecated-design-system'
import {
  BankingBankAccountFragment,
  BankingPayoutBankAccountFragment,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  accountNumberExtraFieldLabel,
  accountNumberLabel,
} from 'modules/banking/lib/bankAccountNumbersHelpers'
import {
  formattedBankAccountNumber,
  visibleAccountNumber,
  visibleExtraField,
} from 'modules/banking/utils'

import { SidepanelAccountingInfo } from './SidepanelAccountingInfo'
import { SidepanelDataPair } from './SidepanelDataPair'
import { SidepanelFooterAction } from './SidepanelFooterAction'
import { SidepanelMemberships } from './SidepanelMemberships'

export interface AccountDetailSidepanelProps {
  bankAccount: BankingBankAccountFragment
  loading: boolean
  disabledSaveButton: boolean
  payoutBankAccount?: BankingPayoutBankAccountFragment
  ledgerAccountId?: number
  legalEntityId: number
  legalEntityName?: string
  url?: string | null
  bankAddress?: string
  companyName?: string
  open: boolean
  hideDeleteButton?: boolean
  onboardingAuthorId?: number
  showLedgerAccountSelector: boolean
  onClose: () => void
  onAccountingInfoChange: (newId: number) => void
  onSaveLedgerAccount: () => void
  onDeleteAccountClick: () => void
  onAddBankAccountMembershipClick: () => void
}

export function AccountDetailSidepanel({
  bankAccount,
  loading,
  disabledSaveButton,
  payoutBankAccount,
  ledgerAccountId,
  legalEntityId,
  legalEntityName,
  bankAddress,
  open,
  url,
  hideDeleteButton,
  onboardingAuthorId,
  showLedgerAccountSelector,
  onClose,
  onAccountingInfoChange,
  onSaveLedgerAccount,
  onDeleteAccountClick,
  onAddBankAccountMembershipClick,
}: AccountDetailSidepanelProps) {
  const extraField = visibleExtraField(bankAccount)
  return (
    <Sidepanel
      width="wide"
      open={open}
      boxPadding={false}
      title={i18n.t('banking.accounts.side_panel.title')}
      onClose={onClose}
      closeLabel={i18n.t('banking.accounts.side_panel.close')}
      footer={
        <SidepanelFooterAction
          hideDeleteButton={hideDeleteButton}
          disabledSave={disabledSaveButton}
          loading={loading}
          onSave={onSaveLedgerAccount}
          onDelete={onDeleteAccountClick}
        />
      }
    >
      <Box padding="s24" gap="s16" position="relative">
        <Box flexDirection="column" justifyContent="spaceBetween" gap="s4">
          <Box flexDirection="column" paddingTop="s24" gap="s16">
            <SidepanelDataPair
              title={i18n.t('banking.accounts.side_panel.legal_entity')}
              desc={legalEntityName ?? ''}
              hideCopyButton
            />
            {bankAccount.beneficiaryName && (
              <SidepanelDataPair
                title={i18n.t('banking.accounts.side_panel.beneficiary_name')}
                desc={bankAccount.beneficiaryName}
                hideCopyButton
              />
            )}
            <SidepanelDataPair
              title={accountNumberLabel(bankAccount.accountNumberType)}
              desc={formattedBankAccountNumber(bankAccount)}
              copyValue={visibleAccountNumber(bankAccount)}
            />
            {extraField && (
              <SidepanelDataPair
                title={accountNumberExtraFieldLabel(bankAccount.accountNumberType) || ''}
                desc={extraField}
                copyValue={extraField}
              />
            )}

            <SidepanelDataPair
              title={i18n.t('banking.accounts.side_panel.currency')}
              desc={bankAccount.currency.toUpperCase()}
              hideCopyButton
            />
            {bankAddress && (
              <SidepanelDataPair
                title={i18n.t('banking.accounts.side_panel.address')}
                desc={bankAddress}
              />
            )}
            {payoutBankAccount && (
              <SidepanelDataPair
                title={i18n.t('banking.accounts.side_panel.external_bank.title')}
                info={i18n.t('banking.accounts.side_panel.external_bank.info')}
                desc={payoutBankAccount.accountNumber.split(/(.{4})/).join(' ')}
                desc2={payoutBankAccount.bankName}
                copyValue={payoutBankAccount.accountNumber}
              />
            )}
            {url && (
              <Box
                paddingTop="s12"
                onClick={() => window.location.replace(url)}
                style={{ cursor: 'pointer' }}
              >
                <Link>{i18n.t('banking.accounts.side_panel.go_to_external_settings')}</Link>
              </Box>
            )}
            {showLedgerAccountSelector && (
              <SidepanelAccountingInfo
                selectedLedgerAccountId={ledgerAccountId}
                onAccountingInfoChange={onAccountingInfoChange}
                legalEntityId={legalEntityId}
                bankAccount={bankAccount}
                disabled={loading}
              />
            )}
            <SidepanelMemberships
              bankAccount={bankAccount}
              notRemomableAccessIds={onboardingAuthorId ? [onboardingAuthorId] : []}
              onAddClick={onAddBankAccountMembershipClick}
            />
            {bankAccount.lastSynchronisedAt && (
              <SidepanelDataPair
                title={i18n.t('banking.accounts.side_panel.last_synchronised_title')}
                desc={convertDate(
                  bankAccount.lastSynchronisedAt,
                  'backend_datetime',
                  'ui_short_with_time'
                )}
                hideCopyButton
              />
            )}
          </Box>
        </Box>
      </Box>
    </Sidepanel>
  )
}
